import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { COLORS, FONTS } from '../../theme/tokens';

const ErrorFallback: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        backgroundColor: COLORS.surfaceApp,
        padding: '24px',
      }}
    >
      <Box
        sx={{
          maxWidth: '440px',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontFamily: FONTS.heading,
            fontSize: '1.5rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: COLORS.navy,
            marginBottom: '12px',
          }}
        >
          {t('Something went wrong')}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONTS.body,
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.5,
            color: COLORS.inkSecondary,
            marginBottom: '24px',
          }}
        >
          {t(
            'An unexpected error occurred. Please reload the page to continue.'
          )}
        </Typography>
        <Button
          variant="contained"
          onClick={() => window.location.reload()}
        >
          {t('Reload page')}
        </Button>
      </Box>
    </Box>
  );
};

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
